import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Product } from '@/mocks/products';

const COL = 'products';

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      const q = query(collection(db, COL), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      const list: Product[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as Product));
      setProducts(list);
    } catch {
      // Firestore index henüz hazır değilse sırasız çek
      const snap = await getDocs(collection(db, COL));
      const list: Product[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as Product));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setProducts(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addProduct = async (product: Omit<Product, 'id' | 'createdAt' | 'order'>) => {
    const imgs = product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []);
    const mainIdx = product.mainImageIndex ?? 0;
    const mainImg = imgs[mainIdx] ?? imgs[0] ?? product.image;
    const newProduct = {
      ...product,
      images: imgs,
      mainImageIndex: mainIdx,
      image: mainImg,
      createdAt: new Date().toISOString().split('T')[0],
      order: products.length + 1,
    };
    const docRef = await addDoc(collection(db, COL), newProduct);
    setProducts(prev => [...prev, { ...newProduct, id: docRef.id } as Product]);
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    const existing = products.find(p => p.id === id);
    if (!existing) return;
    const merged = { ...existing, ...updates };
    const imgs = merged.images && merged.images.length > 0 ? merged.images : [merged.image];
    const mainIdx = merged.mainImageIndex ?? 0;
    merged.image = imgs[mainIdx] ?? imgs[0] ?? merged.image;
    merged.images = imgs;
    const { id: _id, ...data } = merged;
    await updateDoc(doc(db, COL, id), data);
    setProducts(prev => prev.map(p => (p.id === id ? merged : p)));
  };

  const deleteProduct = async (id: string) => {
    await deleteDoc(doc(db, COL, id));
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const reorderProducts = async (reordered: Product[]) => {
    let sliderCounter = 1;
    const withOrder = reordered.map((p, i) => {
      const base = { ...p, order: i + 1 };
      if (p.inSlider) {
        return { ...base, sliderOrder: sliderCounter++ };
      }
      return base;
    });
    setProducts(withOrder);
    const batch = writeBatch(db);
    withOrder.forEach(p => {
      batch.update(doc(db, COL, p.id), { order: p.order, sliderOrder: p.sliderOrder });
    });
    await batch.commit();
  };

  const toggleSlider = async (id: string) => {
    const sliderProducts = products.filter(p => p.inSlider);
    const product = products.find(p => p.id === id);
    if (!product) return null;
    if (!product.inSlider && sliderProducts.length >= 10) {
      return { error: "Slider'a en fazla 10 ürün eklenebilir." };
    }
    const maxOrder = sliderProducts.length > 0
      ? Math.max(...sliderProducts.map(p => p.sliderOrder))
      : 0;
    const updates = {
      inSlider: !product.inSlider,
      sliderOrder: !product.inSlider ? maxOrder + 1 : 0,
    };
    await updateDoc(doc(db, COL, id), updates);
    setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    return null;
  };

  const getSliderProducts = () => {
    return products
      .filter(p => p.inSlider)
      .sort((a, b) => a.sliderOrder - b.sliderOrder)
      .slice(0, 10);
  };

  return {
    products,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    reorderProducts,
    toggleSlider,
    getSliderProducts,
  };
};
